import React, {
  forwardRef,
  useContext,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
} from 'react';
import classNames from 'classnames';
import { Button, Loader, Modal } from 'ncoded-component-library';
import {
  ModalProps,
  ModalRef,
} from 'ncoded-component-library/build/components/organisms/Modal/Modal.component';
import useCallbackRef from 'hooks/useCallbackRef';
import { useTranslation } from 'react-i18next';
import useGetMixpanelProps from 'hooks/useGetMixpanelProps';
import useCallbackAsync from 'hooks/useCallbackAsync';
import PopNotificationsContext from 'providers/PopNotifications/PopNotifications.context';
import CurrentUserContext from 'providers/CurrentUser/CurrentUser.context';
import api from 'api';
import { SUCCESS_MESSAGES } from 'request-responses';
import { getContactNumber, Phone } from 'phoneNumberUtils';
import mixpanelService from 'services/mixpanelService';
import { MobileVerificationInitiatedEventProps } from 'models/MixpanelEvents';
import { User } from 'models/User';
import useCountdown from 'router/subrouters/Auth/hooks/useCountdown';
import {
  NUM_OF_VERIFICATION_PINS,
  RESEND_CODE_COOLDOWN,
  TRANSITION_TIME,
  VERIFIED_PIN_ID,
} from 'data/global';
import { Field, Form } from 'react-final-form';
import PinField from 'router/subrouters/Dash/subrouters/private/subrouters/Profile/pages/ProfilePage/components/PinField';
import { required } from 'validations/general';

import './PhoneVerificationModal.styles.scss';

type PhoneVerificationModalProps = {
  className?: string;
  phone?: Phone;
  disabled?: boolean;
  /**
   * Lets the caller claim errors it renders inline - the number being in use on
   * another account, which offers a transfer rather than a toast, and the 24h
   * block, which both routes can report. Return true to suppress the default
   * error notification.
   */
  onServerError?: (error: unknown, contactNumber: string) => boolean;
  onSuccess?: () => void;
} & ModalProps;

const PhoneVerificationModal: React.ForwardRefRenderFunction<
  ModalRef,
  PhoneVerificationModalProps
> = (props, ref) => {
  const { className, disabled, phone, onServerError, onSuccess, ...rest } =
    props;

  const [modal, modalRef] = useCallbackRef<ModalRef>();

  useImperativeHandle(ref, () => modal, [modal]);

  const classes = classNames('phone-verification-modal', className);

  const { t } = useTranslation();

  const getMixpanelProps = useGetMixpanelProps();

  const { popSuccess, popError } = useContext(PopNotificationsContext);
  const { currentUser, updateCurrentUser } = useContext(CurrentUserContext);

  const contactNumber = useMemo(
    () => getContactNumber(phone || currentUser?.phone),
    [currentUser?.phone, phone],
  );

  // PinField keeps its digits in local state, so remount it to clear the boxes
  // between attempts and resends.
  const [attempt, setAttempt] = useState(0);

  const [handleSubmit, loading] = useCallbackAsync(
    async ({ verificationCode }: { verificationCode: string }) => {
      let data: User;

      try {
        ({ data } = await api.users.checkPhoneVerification(verificationCode));
      } catch (e) {
        setAttempt((old) => old + 1);

        // Both routes report the 24h block, so a limit hit while confirming is
        // handed to the caller too rather than left as a toast over a dead
        // pin field.
        if (onServerError?.(e, contactNumber)) {
          modal?.close();
          return;
        }

        throw e;
      }

      popSuccess(t(SUCCESS_MESSAGES.PHONE_NUMBER_VERIFIED));
      updateCurrentUser(data);
      onSuccess?.();
      modal?.close();
    },
  );

  const { timeLeft, startCountdown } = useCountdown(RESEND_CODE_COOLDOWN);

  const [initVerificationCode, loadingResend] = useCallbackAsync(async () => {
    if (disabled) {
      popError(t('pleaseSetYourPhoneNumber'));
      return;
    }

    try {
      await api.users.phoneVerification(contactNumber);
    } catch (e) {
      // The number belongs to another account: close and let the caller offer
      // the transfer instead of surfacing a dead-end error.
      if (onServerError?.(e, contactNumber)) {
        modal?.close();
        return;
      }

      throw e;
    }

    setAttempt((old) => old + 1);
    startCountdown();
    popSuccess(t(SUCCESS_MESSAGES.CHECK_PHONE_INBOX));
  });

  useEffect(() => {
    if (modal?.openState && !disabled) {
      mixpanelService.mobileVerificationInitiated(
        getMixpanelProps({
          phoneNumber: contactNumber,
        }) as MobileVerificationInitiatedEventProps,
      );

      initVerificationCode();
    }
  }, [
    contactNumber,
    disabled,
    modal?.openState,
    getMixpanelProps,
    initVerificationCode,
  ]);

  useEffect(() => {
    if (modal?.openState) {
      setTimeout(
        () => document.getElementById(VERIFIED_PIN_ID)?.focus(),
        TRANSITION_TIME,
      );
    }
  }, [modal?.openState, attempt]);

  return (
    <Modal
      ref={modalRef}
      className={classes}
      title={t('verifyYourPhoneNumber')}
      {...rest}
    >
      {(loadingResend || loading) && <Loader />}

      <Form onSubmit={handleSubmit}>
        {({ handleSubmit, values: { verificationCode } }) => (
          <form onSubmit={handleSubmit}>
            <p>{t('weHaveSentVerificationCode')}</p>

            <Field
              key={attempt}
              name="verificationCode"
              numOfPins={NUM_OF_VERIFICATION_PINS}
              component={PinField}
              validate={required(t('youMustEnterVerificationCode'))}
            />

            <div className="resend">
              {`${t('didNotGetVerificationCode')} `}
              <Button
                variant="link"
                disabled={loadingResend || timeLeft > 0}
                onClick={initVerificationCode}
              >
                {timeLeft === 0
                  ? t('resendIt')
                  : t('waitFor', { count: timeLeft })}
              </Button>
            </div>

            <div className="actions">
              <Button type="button" variant="link" onClick={modal?.close}>
                {t('cancel')}
              </Button>
              <Button
                type="submit"
                disabled={
                  disabled ||
                  verificationCode?.length !== NUM_OF_VERIFICATION_PINS
                }
              >
                {t('submit')}
              </Button>
            </div>
          </form>
        )}
      </Form>
    </Modal>
  );
};

export default forwardRef(PhoneVerificationModal);
